/**
 * buildSupportLocationText — pure, environment-free helper that renders a copyable
 * "where am I" address for an AI-support handoff from a guide popover.
 *
 * It is deliberately framework- and browser-agnostic: the caller injects the live
 * `href` / `pathname` / `builderApiUrl`, so this can be unit-tested under jest (which
 * has no real `window.location` and no Vite `import.meta.env`). Missing values are
 * omitted line-by-line, so a guide shown OUTSIDE the screen editor still yields a
 * valid URL-only address.
 */

/**
 * Optional in-editor context the screen editor can publish (plain strings only — keeps
 * the `guides` module decoupled from `reactFlow`). Empty when no editor is mounted.
 */
export interface SupportLocationSnapshot {
  /** Project id from app state — preferred over URL parsing when present. */
  projectId?: string;
  /** Human-readable project name (best-effort enrichment). */
  projectName?: string;
  /** Onboarding id from app state — preferred over URL parsing (robust even off the editor route). */
  onboardingId?: string;
  /** Human-readable onboarding name (best-effort enrichment). */
  onboardingName?: string;
  /** The currently-open screen's entity id (NOT in the URL — editor state only). */
  screenId?: string;
  /** The currently-open screen's display name. */
  screenName?: string;
  /** The currently-open screen's StepKind (e.g. "Paywall", "ListSinglePick"). */
  screenType?: string;
  /** The editor section being configured, when known. */
  sectionType?: string;
}

export interface BuildSupportLocationInput {
  /** The guide's stable key — encodes the exact control (e.g. "screenedit-header-appearance-height"). */
  guideKey: string;
  /** The guide's human title (the already-fetched `guide.name`), if available. */
  guideName?: string;
  /** In-editor context, if a SupportLocationProvider is mounted above the popover. */
  snapshot?: SupportLocationSnapshot;
  /** Full current URL (inject `window.location.href`). */
  href?: string;
  /** Current path (inject `window.location.pathname`); used to parse project/onboarding ids. */
  pathname?: string;
  /** Builder API URL (inject `import.meta.env.VITE_BUILDER_API_URL`); used to derive an env label. */
  builderApiUrl?: string;
}

/** Map the builder API URL to a short environment label. */
export function deriveEnvLabel(builderApiUrl?: string): string {
  if (!builderApiUrl) return 'unknown';
  const u = builderApiUrl.toLowerCase();
  if (u.includes('localhost') || u.includes('127.0.0.1')) return 'local';
  if (u.includes('stage')) return 'stage';
  if (u.includes('dev-api') || u.includes('dev.') || u.includes('devsegmently')) return 'dev';
  if (u.includes('segmently')) return 'prod';
  try {
    return new URL(builderApiUrl).host;
  } catch {
    return 'unknown';
  }
}

/** Parse the location-hierarchy ids out of a route path. All optional. */
export function parseLocationIds(pathname?: string): {
  projectId?: string;
  onboardingId?: string;
  strategyId?: string;
  blockId?: string;
} {
  if (!pathname) return {};
  return {
    projectId: pathname.match(/\/project\/([^/?#]+)/)?.[1],
    onboardingId: pathname.match(/\/onboarding-v2\/([^/?#]+)/)?.[1],
    strategyId: pathname.match(/\/strategy\/([^/?#]+)/)?.[1],
    blockId: pathname.match(/\/block\/([^/?#]+)/)?.[1],
  };
}

/** Build an `id`/`name` line, omitting it entirely when neither is present. */
function entityLine(label: string, id?: string, name?: string): string | null {
  if (!id && !name) return null;
  if (id && name) return `${label}: ${name} (${id})`;
  if (name) return `${label}: ${name}`;
  return `${label} id: ${id}`;
}

/**
 * Render the labeled support-location text block. Only lines with data are included.
 */
export function buildSupportLocationText(input: BuildSupportLocationInput): string {
  const { guideKey, guideName, snapshot = {}, href, pathname, builderApiUrl } = input;
  const ids = parseLocationIds(pathname);

  // App-state ids (from the editor provider) win over URL parsing — robust even when
  // the route doesn't carry the id (e.g. modal/list surfaces).
  const projectId = snapshot.projectId ?? ids.projectId;
  const onboardingId = snapshot.onboardingId ?? ids.onboardingId;

  const lines: Array<string | null> = [
    'Segmently support location',
    `env: ${deriveEnvLabel(builderApiUrl)}`,
    entityLine('project', projectId, snapshot.projectName),
    entityLine('onboarding', onboardingId, snapshot.onboardingName),
    ids.strategyId ? entityLine('strategy', ids.strategyId) : null,
    ids.blockId ? entityLine('block', ids.blockId) : null,
    buildScreenLine(snapshot),
    snapshot.sectionType ? `section: ${snapshot.sectionType}` : null,
    `setting: ${guideName || '(unknown)'} (guideKey: ${guideKey})`,
    href ? `url: ${href}` : null,
  ];

  return lines.filter((line): line is string => Boolean(line)).join('\n');
}

/** Combine the screen name · type (id) into a single line, omitting absent parts. */
function buildScreenLine(snapshot: SupportLocationSnapshot): string | null {
  const { screenId, screenName, screenType } = snapshot;
  if (!screenId && !screenName && !screenType) return null;
  const head = [screenName, screenType].filter(Boolean).join(' · ');
  if (head && screenId) return `screen: ${head} (${screenId})`;
  if (head) return `screen: ${head}`;
  return `screen id: ${screenId}`;
}
